import React, { useEffect, useState } from 'react'
import { router } from '@inertiajs/react'
import { AlertCircle, Building2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import HotelAccessPicker, { draftFromAccess, draftIsEmpty, propertyIdsFor } from '@/components/account/HotelAccessPicker'
import type { HotelAccessDraft, HotelPicker, TeamMember } from '@/types/team'

interface HotelAccessDialogProps {
  /** `/a/:account_slug/settings`, served by the backend. The save is this + `/team/:id/hotels`. */
  settingsPath: string
  businessName: string
  picker: HotelPicker
  /** The teammate whose hotels are being changed; null closes the dialog. */
  member: TeamMember | null
  onClose: () => void
}

// "Change hotels" on a manager or viewer (change safety R4.5, §5.17.3).
//
// Opens on the teammate's current access, saves through the same Inertia PATCH every Team action
// uses, and closes only when the server accepted it: the flash then says, in the server's words,
// what they can open now. A list the server refuses (another tab changed the business, half a
// combined inventory) comes back as `errors.hotelAccess` and is shown here, in the dialog, with the
// draft intact. Read from the request's own onError, never from the page's global errors, so an
// unrelated form's error can never appear in this dialog (the PausePushDialog rule).
//
// 480px like every form dialog. The fade and 150 ms scale are ui/dialog's own (operator.css).
export default function HotelAccessDialog({ settingsPath, businessName, picker, member, onClose }: HotelAccessDialogProps) {
  const [draft, setDraft] = useState<HotelAccessDraft>({ scope: 'all', selected: [] })
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  // Reset when a teammate's dialog OPENS, and only then. Keyed on the id, not on the props: a refused
  // save reloads the page's props, and resetting on that would wipe the error and the draft the
  // person is about to correct.
  const openFor = member?.membershipId ?? null
  useEffect(() => {
    if (openFor === null) return
    setDraft(draftFromAccess(member?.hotelAccess ?? null, picker.options))
    setError(null)
    setSaving(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openFor])

  function save(event: React.FormEvent) {
    event.preventDefault()
    if (!member || saving || draftIsEmpty(draft)) return

    const propertyIds = propertyIdsFor(draft, picker.options)
    router.patch(
      `${settingsPath}/team/${member.membershipId}/hotels`,
      { hotel_access: draft.scope, property_ids: propertyIds ?? [] },
      {
        preserveScroll: true,
        preserveState: true,
        onStart: () => {
          setSaving(true)
          setError(null)
        },
        onError: (errors) => {
          setError((errors as Record<string, string>).hotelAccess ?? "These hotels couldn't be saved. Try again.")
        },
        onSuccess: (page) => {
          const errors = (page.props as { errors?: Record<string, string> }).errors
          if (errors?.hotelAccess) return setError(errors.hotelAccess)
          onClose()
        },
        onFinish: () => setSaving(false),
      }
    )
  }

  return (
    <Dialog open={member !== null} onOpenChange={(open) => !open && !saving && onClose()}>
      <DialogContent className="sm:max-w-[480px]">
        <form onSubmit={save} className="flex min-h-0 flex-1 flex-col">
          <DialogHeader>
            <DialogTitle>Hotels for {member?.email}</DialogTitle>
            <DialogDescription className="flex items-center gap-1.5 text-xs">
              <Building2 className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              <span>{businessName}</span>
            </DialogDescription>
          </DialogHeader>

          <DialogBody className="space-y-4">
            {error && (
              <div role="alert" className="flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2.5">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-rose-600" aria-hidden="true" />
                <p className="text-xs font-medium text-rose-700">{error}</p>
              </div>
            )}
            <HotelAccessPicker picker={picker} draft={draft} onChange={setDraft} disabled={saving} />
          </DialogBody>

          <DialogFooter>
            <Button type="button" variant="ghost" className="cursor-pointer rounded-lg" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" className="cursor-pointer rounded-lg" disabled={saving || draftIsEmpty(draft)} aria-busy={saving}>
              {saving ? 'Saving hotels…' : 'Save hotels'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
